import { CostCenter, Product } from '@portal/portal-core';
import { startCase } from 'lodash-es';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import { FjTooltip } from 'components/primitives';
import { Button } from 'components/styled-primitives';
import FjSortTable from 'components/table/fj-sort-table';
import { CheckboxCell, CostCenterCell, DateTimeCell } from 'components/table/table-cells';
import TableCheckbox from 'components/table/table-checkbox';
import { compareDates, compareInts } from 'utils';

import { CloudAdminActions, SuperAdminActions } from 'actions';
import { MODAL_TYPES } from 'constants/super-admin';
import { selectActiveLicenseRows, selectLicenseRequestProgress } from 'selectors/super-admin';
import { useAppDispatch, useAppSelector } from 'store/hooks';

// A single enabled/disabled product feature entry within a license.
interface LicenseFeature {
  enabled?: boolean;
  name?: string;
}

// Minimal shape of a license row's product-access sub-object (fields read here).
interface LicenseProductAccess {
  id: string;
  name: string;
  status?: string;
  type?: string;
  maxActiveUsers?: number;
  maxSharedComputers?: number;
  activityState?: string;
  startDate?: string;
  endDate?: string;
  features?: Record<string, LicenseFeature>;
}

// Minimal license row shape — only the fields accessed by the table cells.
interface LicenseRow {
  invitesFetchFailed?: boolean;
  costCenter: { id: string; name: string; countryCode?: string };
  productAccess: LicenseProductAccess;
  admin: { primaryEmail?: string };
  allocatedSeats?: number;
  activeSharedSeats?: number;
}

// react-table-6-style render data the column builder maps each cell/cellProps
// call back into ({ value, original, path }).
interface CellRenderData<V = unknown> {
  value: V;
  path?: string;
  original: LicenseRow;
}

interface InstitutionalLicenseTableProps {
  licenses: LicenseRow[];
  filterControls?: React.ReactNode;
  controls?: React.ReactNode;
  syncFailed?: boolean;
  loading?: boolean;
  percentLoaded?: number;
  currentRequest?: number;
  requestsCount?: number;
  nextRequest?: number;
  dismissed?: boolean;
  onRetrySync?: () => void;
  onDismissSyncPrompt?: () => void;
  // For the search view: server pagination (over cost centers, via LicenseSearchControls)
  // already governs what rows this component is handed, so FjSortTable's own client-side
  // Rows/Page chrome — which would otherwise re-paginate that already-small, already-final set
  // of license rows a second time — is redundant. hidePagination hides that chrome;
  // defaultPageSize must be large enough that FjSortTable's own (still-active, just hidden)
  // client pagination never truncates rows out of view now that there's no way to page to them.
  hidePagination?: boolean;
  defaultPageSize?: number;
}

const PaddedTable = styled(FjSortTable)`
  padding-left: ${({ theme }) => theme.grid.units(1)};
`;

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  /* A flex item defaults to min-width: auto, which refuses to shrink below the
     content's own width. Without this the table can never scroll horizontally —
     it just grows past its container and gets clipped by whatever is upstream. */
  min-width: 0;
  min-height: 360px;
  height: 100%;
`;

const SyncFailedOverlay = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: 12px 0 0;
  background: rgba(142, 174, 178, 0.12);
  border: 1px solid rgba(35, 31, 32, 0.12);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-sizing: border-box;
`;

const SyncFailedContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 720px;
  padding: 16px;
  text-align: center;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.selectableLightBg};
    line-height: 1.4;
  }

  button + button {
    margin-left: 0;
  }
`;

const InstitutionalLicenseTable = ({
  licenses,
  filterControls,
  controls,
  syncFailed = false,
  loading = false,
  percentLoaded,
  currentRequest = 0,
  requestsCount = 0,
  nextRequest = 1,
  dismissed = false,
  onRetrySync,
  onDismissSyncPrompt,
  hidePagination = false,
  defaultPageSize,
}: InstitutionalLicenseTableProps): React.ReactElement => {
  const selectedRows = useAppSelector(selectActiveLicenseRows);
  const requestProgress = useAppSelector(selectLicenseRequestProgress);

  const dispatch = useAppDispatch();
  const selectRow = (licenseId: string, costCenterId: string): void => {
    dispatch(SuperAdminActions.selectLicenseRow(licenseId, costCenterId));
  };
  const deselectRow = (id: string): void => {
    dispatch(SuperAdminActions.deselectLicenseRow(id));
  };
  const openLicenseDetailsModal = (modalType: string, id: string): void => {
    dispatch(CloudAdminActions.showModal(modalType));
    dispatch(SuperAdminActions.selectLicenseDetails(id));
  };

  const getColumns = (): Record<string, unknown>[] => {
    const invitesFetchFailed = licenses.length > 0 ? licenses[0].invitesFetchFailed : false;
    const defaults = {
      cellProps: ({
        path,
        original: {
          costCenter: { name: costCenterName },
          productAccess: { name: product },
        },
      }: CellRenderData) => ({
        dataTestId: `${path}__${costCenterName}__${product.toLowerCase()}`,
      }),
    };
    return [
      {
        id: 'select',
        path: 'productAccess.id',
        Cell: ({
          value,
          original: {
            costCenter: { id: costCenterID },
          },
        }: CellRenderData<string>) => (
          <TableCheckbox
            rowId={value}
            selectRow={() => selectRow(value, costCenterID)}
            deselectRow={deselectRow}
            selectedRows={selectedRows}
          />
        ),
        CellContainer: CheckboxCell,
        width: 40,
        resizable: false,
        sortable: false,
        ...defaults,
      },
      {
        path: 'costCenter.name',
        Header: () => 'Site',
        Cell: (
          {
            value,
            original: {
              admin: { primaryEmail },
              productAccess: { id },
            },
          }: CellRenderData<string>,
          props: Record<string, unknown>,
        ) => (
          <CostCenterCell
            email={primaryEmail}
            costCenter={value}
            onClick={() => openLicenseDetailsModal(MODAL_TYPES.LICENSE_DETAILS, id)}
            {...props}
          />
        ),
        CellContainer: Fragment,
        alignLeft: true,
        minWidth: 130,
        ...defaults,
      },
      {
        path: 'productAccess.status',
        Header: () => 'Status',
        width: 74,
        minWidth: 74,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        ...defaults,
      },
      {
        path: 'productAccess.name',
        Header: () => 'Product',
        Cell: ({ value }: CellRenderData<string>) => Product.PRODUCT_DISPLAY_NAMES[value],
        minWidth: 45,
        ...defaults,
      },
      {
        id: 'productAccessId',
        path: 'productAccess.id',
        Header: () => 'ProductAccessId',
        minWidth: 70,
        ...defaults,
        resizable: false,
      },
      {
        path: 'productAccess.type',
        Header: () => 'License Type',
        minWidth: 48,
        ...defaults,
      },
      {
        path: 'allocatedSeats',
        Header: (): React.ReactNode =>
          invitesFetchFailed ? (
            <FjTooltip
              Component={
                <span>Allocated seats may not include pending invites due to a fetch error</span>
              }
            >
              <span>Allocated Seats*</span>
            </FjTooltip>
          ) : (
            'Allocated Seats'
          ),
        Cell: ({
          value,
          original: { invitesFetchFailed: rowInvitesFetchFailed },
        }: CellRenderData<number>): React.ReactNode => {
          if (rowInvitesFetchFailed) {
            return (
              <FjTooltip
                Component={
                  <span>Allocated seats may not include pending invites due to a fetch error</span>
                }
              >
                <span>{value}*</span>
              </FjTooltip>
            );
          }
          return value;
        },
        width: 84,
        minWidth: 84,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        sortMethod: (a: unknown, b: unknown): number => {
          // Handle numeric comparison properly, including 0
          const numA = typeof a === 'number' ? a : 0;
          const numB = typeof b === 'number' ? b : 0;
          return numA - numB;
        },
        ...defaults,
      },
      {
        path: 'activeSharedSeats',
        Header: () => 'Active Shared',
        Cell: ({ value }: CellRenderData<number>) => value,
        width: 70,
        minWidth: 70,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        sortMethod: (a: unknown, b: unknown): number => {
          const numA = typeof a === 'number' ? a : 0;
          const numB = typeof b === 'number' ? b : 0;
          return numA - numB;
        },
        ...defaults,
      },
      {
        path: 'productAccess.maxActiveUsers',
        Header: () => 'Max Seats',
        width: 70,
        minWidth: 70,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        sortMethod: compareInts,
        ...defaults,
      },
      {
        path: 'productAccess.maxSharedComputers',
        Header: () => 'Max Shared',
        width: 70,
        minWidth: 70,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        sortMethod: compareInts,
        ...defaults,
      },
      {
        path: 'productAccess.activityState',
        Header: () => 'Activity State',
        Cell: ({ value }: CellRenderData<string>) =>
          value === CostCenter.ACTIVITY_STATES.suspended ? (
            <i>{startCase(value)}</i>
          ) : (
            startCase(value)
          ),
        width: 74,
        minWidth: 74,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        ...defaults,
      },
      {
        path: 'productAccess.startDate',
        Header: () => 'Start Date',
        Cell: DateTimeCell,
        sortMethod: compareDates,
        width: 92,
        minWidth: 92,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        ...defaults,
      },
      {
        path: 'productAccess.endDate',
        Header: () => 'End Date',
        Cell: DateTimeCell,
        sortMethod: compareDates,
        width: 92,
        minWidth: 92,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        ...defaults,
      },
      {
        path: 'costCenter.countryCode',
        Header: () => 'Country Code',
        width: 74,
        minWidth: 74,
        resizable: false,
        headerProps: () => ({ wrapHeader: true }),
        ...defaults,
      },
      {
        path: 'productAccess.features',
        Header: () => 'Features',
        Cell: ({ value }: CellRenderData<Record<string, LicenseFeature> | undefined>) => {
          if (!value || Object.keys(value).length === 0) return '—';
          const enabledFeatures = Object.values(value)
            .filter((feature) => feature.enabled)
            .map((feature) => feature.name)
            .join(', ');
          return enabledFeatures || '—';
        },
        minWidth: 80,
        ...defaults,
      },
    ];
  };

  const hasIncompleteSync = requestsCount > 0 ? currentRequest < requestsCount : syncFailed;
  const shouldRenderSyncPrompt =
    !dismissed &&
    syncFailed &&
    hasIncompleteSync &&
    (onRetrySync || onDismissSyncPrompt);
  const effectiveLoading =
    !dismissed &&
    (loading || (!syncFailed && requestsCount > 0 && currentRequest < requestsCount));
  const effectivePercentLoaded = percentLoaded ?? requestProgress ?? 100;
  const progressText =
    requestsCount > 0
      ? 'Sync was interrupted while loading license data. Restart sync?'
      : 'We could not finish syncing license data. Restart sync?';

  return (
    <TableContainer>
      <PaddedTable
        columns={getColumns()}
        data={licenses}
        selectedRowsByID={selectedRows}
        filterControls={filterControls}
        controls={controls}
        loading={effectiveLoading}
        percentLoaded={Math.min(Math.max(effectivePercentLoaded, 0), 100)}
        tableName="sa-institutional-licenses"
        hidePagination={hidePagination}
        defaultPageSize={defaultPageSize}
      />
      {shouldRenderSyncPrompt && (
        <SyncFailedOverlay data-testid="institutional-license-sync-failed">
          <SyncFailedContent>
            <p>{progressText}</p>
            {onRetrySync && <Button onClick={onRetrySync}>Restart Data Sync</Button>}
            {onDismissSyncPrompt && (
              <Button onClick={onDismissSyncPrompt}>Use Partially Loaded Data</Button>
            )}
          </SyncFailedContent>
        </SyncFailedOverlay>
      )}
    </TableContainer>
  );
};

export default InstitutionalLicenseTable;
